/**
 * Hướng theo KHÍ NGÀY — lớp luận đổi mỗi ngày, khác hẳn Bát Trạch.
 *
 * - Bát Trạch (`lib/bat-trach.ts`): theo CUNG PHI của người xem → cố định cả đời.
 * - Khí ngày (file này): theo NGŨ HÀNH CHI NGÀY → đổi từng ngày, ai cũng như nhau.
 *
 * Tên và góc của 8 hướng lấy thẳng từ `HUONG_8` để hai la bàn không bao giờ lệch
 * nhau; file này chỉ thêm lớp ngũ hành phương vị + quan hệ sinh/khắc.
 *
 * Bảng tra cố ý để lộ ra cho dễ chỉnh, giống tuvi_score/compat_data bên backend.
 */
import { HUONG_8, type Huong } from "@/lib/bat-trach";

export type NguHanh = "Kim" | "Mộc" | "Thủy" | "Hỏa" | "Thổ";

/** Ngũ hành sinh: Mộc → Hỏa → Thổ → Kim → Thủy → Mộc */
export const SINH: Record<NguHanh, NguHanh> = {
  Mộc: "Hỏa",
  Hỏa: "Thổ",
  Thổ: "Kim",
  Kim: "Thủy",
  Thủy: "Mộc",
};

/** Ngũ hành khắc: Mộc khắc Thổ, Thổ khắc Thủy, Thủy khắc Hỏa, Hỏa khắc Kim, Kim khắc Mộc */
export const KHAC: Record<NguHanh, NguHanh> = {
  Mộc: "Thổ",
  Thổ: "Thủy",
  Thủy: "Hỏa",
  Hỏa: "Kim",
  Kim: "Mộc",
};

/** Ngũ hành phương vị theo Hậu thiên bát quái. */
export const HANH_THEO_HUONG: Record<Huong, NguHanh> = {
  Bắc: "Thủy",
  "Đông Bắc": "Thổ",
  Đông: "Mộc",
  "Đông Nam": "Mộc",
  Nam: "Hỏa",
  "Tây Nam": "Thổ",
  Tây: "Kim",
  "Tây Bắc": "Kim",
};

export type MucKhi = "hop" | "binh" | "ky";

export const MUC_KHI_META: Record<
  MucKhi,
  { nhan: string; mau: string; diem: string; bien: string; dam: number }
> = {
  hop: {
    nhan: "Hợp",
    mau: "text-element-wood",
    diem: "bg-element-wood",
    bien: "--element-wood",
    dam: 0.22,
  },
  binh: {
    nhan: "Bình",
    mau: "text-muted-foreground",
    diem: "bg-muted-foreground/40",
    bien: "--muted-foreground",
    dam: 0.1,
  },
  ky: {
    nhan: "Kỵ",
    mau: "text-destructive",
    diem: "bg-element-fire",
    bien: "--element-fire",
    dam: 0.2,
  },
};

/** Quan hệ giữa hành của hướng (X) và hành khí ngày (D). */
export function xepLoaiKhi(huong: NguHanh, ngay: NguHanh): { muc: MucKhi; ly_do: string } {
  if (SINH[huong] === ngay) return { muc: "hop", ly_do: `${huong} sinh ${ngay} — được trợ khí` };
  if (huong === ngay) return { muc: "hop", ly_do: `${huong} tỷ hòa với khí ngày` };
  if (KHAC[huong] === ngay) return { muc: "ky", ly_do: `${huong} khắc ${ngay} — nên tránh` };
  if (SINH[ngay] === huong)
    return { muc: "binh", ly_do: `${ngay} sinh ${huong} — hao khí đôi chút` };
  return { muc: "binh", ly_do: `${ngay} khắc ${huong} — không được lợi nhiều` };
}

export type HuongKhiLuan = {
  ten: Huong;
  goc: number;
  hanh: NguHanh;
  muc: MucKhi;
  ly_do: string;
};

/** Luận đủ 8 hướng theo khí ngày, giữ thứ tự Bắc → Tây Bắc. */
export function luanHuongKhiNgay(khiNgay: NguHanh): HuongKhiLuan[] {
  return HUONG_8.map((h) => {
    const hanh = HANH_THEO_HUONG[h.ten];
    const { muc, ly_do } = xepLoaiKhi(hanh, khiNgay);
    return { ten: h.ten, goc: h.goc, hanh, muc, ly_do };
  });
}

/** Bóc tên ngũ hành đầu tiên trong một chuỗi (vd "Mộc ứng Hỏa" → "Mộc"). */
export function nguHanhTuChuoi(raw?: string | null): NguHanh | null {
  const found = (raw ?? "").match(/Kim|Mộc|Thủy|Hỏa|Thổ/g) as NguHanh[] | null;
  return found?.[0] ?? null;
}
